// src/pages/business/BusinessDashboard.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, ChevronDown, Building2,
  Phone, Mail, QrCode, Pencil, ExternalLink,
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { getImageUrl } from '@/config/media';
import OptionSquareCard from '@/components/business/OptionSquareCard';
import BusinessHealthBar from '@/components/business/BusinessHealthBar';
import AIChatButton from '@/components/business/AIChatButton';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import BusinessNotificationsPanel from '@/components/business/BusinessNotificationsPanel';
import { useToast } from '@/contexts/ToastContext';
import { useBusinessStore } from '@/stores/useBusinessStore';

interface Business {
  id: string;
  name: string;
  category: string;
  description?: string;
  logo_url?: string;
  business_email?: string;
  primary_contact_phone?: string;
}

// ─── exact option definitions from mobile businessOptions array ───────────────
// title, icon and navigation path map 1-to-1 with mobile screens
const buildOptions = (id: string) => ({

  // renderSection("Business Management", getCategoryOptions("management"))
  // mobile management items: Majengo Profile, Services, Products, Gallery
  // Staff Management has category "operations" in mobile source so appears in
  // Daily Operations — but you see it in Business Management on screen because
  // the mobile renders the Majengo Profile *card* first (separate from the grid)
  // then the grid for management which on YOUR device shows:
  // Your Business Profile | Majengo Profile | Services | Products | Staff Management
  // "Your Business Profile" is the business header card, not a grid option.
  // Staff Management is category "operations" — it shows at top of Daily Operations.
  // The 4 management grid cards are: Majengo Profile, Services, Products, Gallery.
  management: [
    { title: 'Your Business Profile', icon: 'business-outline',   path: `/business/${id}/details`,    category: 'management' },
    { title: 'Majengo Profile',       icon: 'qr-code-outline',    path: `/business/${id}/edit`,       category: 'management' },
    { title: 'Services',              icon: 'pricetags-outline',  path: `/business/${id}/services`,   category: 'management' },
    { title: 'Products',              icon: 'pricetags-outline',  path: `/business/${id}/products`,   category: 'management' },
    { title: 'Staff Management',      icon: 'people-outline',     path: `/business/${id}/staff`,      category: 'management' },
  ],

  // renderMostUsed(getCategoryOptions("operations").slice(0, 4))
  // title on mobile: "Most used"
  // first 4 of operations: Bookings, Inventory, Customer Reviews, Customer Chats
  mostUsed: [
    { title: 'Customer Orders',   icon: 'orders-outline',        path: `/business/${id}/orders`,     category: 'operations', notificationCount: 2 },
    { title: 'Bookings',          icon: 'calendar-outline',      path: `/business/${id}/bookings`,   category: 'operations', notificationCount: 3 },
    { title: 'Inventory',         icon: 'cube-outline',          path: `/business/${id}/barcodes`,   category: 'operations' },
    { title: 'Customer Chats',    icon: 'chatbubbles-outline',   path: `/business/${id}/chats`,      category: 'operations' },
  ],

  // renderSection("Daily Operations", getCategoryOptions("operations"))
  // all operations items
  operations: [
    { title: 'Bookings',          icon: 'calendar-outline',      path: `/business/${id}/bookings`,   category: 'operations', notificationCount: 3 },
    { title: 'Inventory',         icon: 'cube-outline',          path: `/business/${id}/barcodes`,   category: 'operations' },
    { title: 'Customer Chats',    icon: 'chatbubbles-outline',   path: `/business/${id}/chats`,      category: 'operations' },
    { title: 'Customer Reviews',  icon: 'star-outline',          path: `/business/${id}/reviews`,    category: 'operations', notificationCount: 5 },
    { title: 'Live POS',            icon: 'cart-outline',          path: `/business/${id}/products`,   category: 'operations' },
    { title: 'Payments',          icon: 'cash-outline',          path: `/business/${id}/mpesa`,      category: 'operations' },
    { title: 'Payment Links',     icon: 'link-outline',          path: `/business/${id}/payment-links`, category: 'operations' },
  ],

  // renderSection("Analytics & Reports", getCategoryOptions("analytics"))
  // renderSection("Marketing & Growth", getCategoryOptions("marketing"))
  // combined into "Analytics & Growth" section as you described:
  // Business Reports (Business Insights), Promotions, Marketing Tools (Marketing)
  analyticsAndGrowth: [
    { title: 'Business Reports',  icon: 'analytics-outline',     path: `/business/${id}/reports`,    category: 'analytics' },
    { title: 'Promotions',        icon: 'megaphone-outline',     path: `/business/${id}/promotions`, category: 'marketing' },
    { title: 'Marketing Tools',   icon: 'trending-up-outline',   path: `/business/${id}/marketing`,  category: 'marketing', isPremium: true },
  ],
});

// Per-section accent colours — mirrors mobile accentColor logic in renderSquare:
// management → colors.primary (#4F46E5)
// operations  → colors.success (#10B981)
// analytics   → colors.secondary (#0EA5E9)
// marketing   → #8e44ad
// The gradient on each card is: linear-gradient(135deg, accentColor, #4F46E5)
// so each section produces a distinct gradient border tone
const accent: Record<string, string> = {
  management: '#4F46E5', // indigo   → indigo  (same-hue, subtle)
  operations: '#10B981', // emerald  → indigo
  analytics:  '#0EA5E9', // sky      → indigo
  marketing:  '#8e44ad', // purple   → indigo
};

// ─── Section wrapper card
// No border — elevated card bg creates depth against page bg (same as BusinessSelectionPage)
// overflow-visible is critical so gradient borders and notification badges are never clipped
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mx-4 mt-5 rounded-2xl bg-light-card dark:bg-dark-card shadow-soft" style={{ overflow: 'visible' }}>
    <p className="px-5 pt-5 pb-1 text-xl font-bold text-light-text dark:text-dark-text">{title}</p>
    {children}
  </div>
);

// ─── Grid of OptionSquareCards
// padding on ALL sides so gradient borders of edge cards are never clipped
const OptionGrid: React.FC<{
  items: { title: string; icon: string; path: string; category: string; notificationCount?: number; isPremium?: boolean }[];
  onNav: (path: string) => void;
}> = ({ items, onNav }) => (
  <div
    className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-7 gap-4"
    style={{ overflow: 'visible', padding: '8px 16px 20px 16px' }}
  >
    {items.map(opt => (
      <OptionSquareCard
        key={opt.title}
        title={opt.title}
        icon={opt.icon}
        accentColor={accent[opt.category]}
        notificationCount={opt.notificationCount}
        isPremium={opt.isPremium}
        onPress={() => onNav(opt.path)}
      />
    ))}
  </div>
);

// ─── Main
export const BusinessDashboard: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { error: toastError } = useToast();
  const { activeBusiness, setActiveBusiness } = useBusinessStore();

  const [business, setBusiness]             = useState<Business | null>(
    activeBusiness?.id === businessId ? (activeBusiness as any) : null
  );
  const [loading, setLoading]               = useState(!business);
  const [refreshing, setRefreshing]         = useState(false);
  const [error, setError]                   = useState<string | null>(null);
  const [showPicker, setShowPicker]         = useState(false);
  const [allBusinesses, setAllBusinesses]   = useState<Business[]>([]);
  const [profileSlug, setProfileSlug]       = useState<string | null>(null);
  const [showTimeoutError, setShowTimeoutError] = useState(false);

  // GET /api/businesses/:id
const fetchBusiness = useCallback(async () => {
  if (!businessId) {
    console.error('[BusinessDashboard] No businessId provided');
    setError('No business ID provided');
    setLoading(false);
    return;
  }
  
  console.log('[BusinessDashboard] Fetching business:', businessId);
  
  try {
    setError(null);
    const res = await apiClient.get(`/businesses/${businessId}`);
    console.log('[BusinessDashboard] Business fetched successfully:', res.data);
    
    if (res.data) {
      setBusiness(res.data);
      setActiveBusiness(res.data);
    } else {
      console.error('[BusinessDashboard] No business data in response');
      setError('Business not found');
    }
  } catch (err: any) {
    console.error('[BusinessDashboard] Fetch error:', err);
    const errorMsg = err?.response?.data?.error || err?.message || 'Failed to load business.';
    setError(errorMsg);
    toastError('Error', errorMsg);
  } finally {
    console.log('[BusinessDashboard] Fetch complete, setting loading to false');
    setLoading(false);
    setRefreshing(false);
  }
}, [businessId, toastError]);

  // GET /api/businesses/:id/profile  — profile slug for "View Live" button
const fetchProfileSlug = useCallback(async () => {
  if (!businessId) return;
  try {
    // Try to get profile info from the main business endpoint first
    const res = await apiClient.get(`/businesses/${businessId}`);
    if (res.data?.profile_slug || res.data?.slug) {
      setProfileSlug(res.data.profile_slug || res.data.slug);
    }
  } catch { /* no profile yet — View Live stays disabled */ }
}, [businessId]);

  // GET /api/businesses — populate business switcher
const fetchAllBusinesses = useCallback(async () => {
  try {
    const res = await apiClient.get<Business[]>('/businesses');
    setAllBusinesses(res.data || []);
  } catch (err) {
    console.error('Error fetching businesses:', err);
    // Silent fail
  }
}, []);

  useEffect(() => {
    fetchBusiness();
    fetchProfileSlug();
    fetchAllBusinesses();
  }, [fetchBusiness, fetchProfileSlug, fetchAllBusinesses]);

  // Timeout detection for loading state
  useEffect(() => {
    if (loading && !business) {
      const timer = setTimeout(() => {
        setShowTimeoutError(true);
      }, 10000);
      return () => clearTimeout(timer);
    } else {
      setShowTimeoutError(false);
    }
  }, [loading, business]);

  const opts = buildOptions(businessId!);

  // ── Loading skeleton
  if (loading && !business) {
    if (showTimeoutError) {
      return (
        <div className="min-h-screen bg-light-bg dark:bg-dark-bg flex flex-col items-center justify-center gap-4 px-8 text-center">
          <Building2 size={56} className="text-red-500" />
          <p className="text-xl font-bold text-light-text dark:text-dark-text">Backend Server Not Responding</p>
          <p className="text-sm text-light-subtle dark:text-dark-subtle max-w-md">
            The backend server at <code className="bg-light-card dark:bg-dark-card px-2 py-1 rounded">http://localhost:3001</code> is not responding.
            <br /><br />
            Please make sure the backend server is running:
            <br />
            <code className="block mt-2 bg-light-card dark:bg-dark-card px-3 py-2 rounded text-left">cd mejengo\ back\ server<br />npm run dev</code>
          </p>
          <button onClick={() => window.location.reload()} className="px-5 py-2 rounded-xl bg-primary text-white text-sm font-semibold">Retry</button>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg p-4 max-w-5xl mx-auto space-y-4">
        {/* top two-column row */}
        <div className="flex gap-4">
          <div className="flex-1 rounded-2xl bg-light-card dark:bg-dark-card animate-pulse" style={{ height: 160 }} />
          <div className="w-64 flex-shrink-0 rounded-2xl bg-light-card dark:bg-dark-card animate-pulse" style={{ height: 160 }} />
        </div>
        {[180, 160, 200, 140, 140].map((h, i) => (
          <div key={i} className="rounded-2xl bg-light-card dark:bg-dark-card animate-pulse" style={{ height: h }} />
        ))}
      </div>
    );
  }

  // ── Error
  if (error && !business) return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg flex flex-col items-center justify-center gap-4 px-8 text-center">
      <Building2 size={56} className="text-light-subtle dark:text-dark-subtle" />
      <p className="text-sm text-light-subtle dark:text-dark-subtle">{error}</p>
      <button onClick={fetchBusiness} className="px-5 py-2 rounded-xl bg-primary text-white text-sm font-semibold">Retry</button>
      <button onClick={() => navigate('/business')} className="text-sm text-light-subtle dark:text-dark-subtle underline">Go Back</button>
    </div>
  );

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg">

      {/* ── Header ── */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card shadow-soft">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">

          <button
            onClick={() => navigate('/business')}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-light-bg dark:bg-dark-bg hover:opacity-75 transition-opacity flex-shrink-0"
          >
            <ArrowLeft size={20} className="text-light-text dark:text-dark-text" />
          </button>

          {/* business name + logo — tapping opens switcher */}
          <button
            onClick={() => setShowPicker(true)}
            className="flex-1 flex items-center gap-3 min-w-0"
          >
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center overflow-hidden flex-shrink-0">
              {business?.logo_url
                ? <img src={getImageUrl(business.logo_url)} alt={business.name} className="w-full h-full object-cover" />
                : <span className="text-white font-bold">{business?.name?.charAt(0).toUpperCase() || 'B'}</span>
              }
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="font-bold text-light-text dark:text-dark-text truncate">{business?.name || 'Business Name'}</p>
              <p className="text-xs text-light-subtle dark:text-dark-subtle truncate">{business?.category || 'Category'}</p>
            </div>
            <ChevronDown size={15} className="text-light-subtle dark:text-dark-subtle flex-shrink-0" />
          </button>

          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => { setRefreshing(true); fetchBusiness(); fetchProfileSlug(); }}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
            >
              <RefreshCw size={15} className={`text-light-subtle dark:text-dark-subtle ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            {businessId && <BusinessNotificationsPanel businessId={businessId} />}
            <ThemeToggle />
          </div>
        </div>
      </div>

      <div className="w-full px-0">

        {/* ── Two-column top row: Business Health (left) | Majengo Profile (right) ── */}
        <div className="mx-6 mt-6 flex gap-6 items-stretch">

          {/* LEFT — Business Health */}
          <div className="flex-1 min-w-0 rounded-2xl bg-light-card dark:bg-dark-card shadow-soft overflow-hidden">
            {businessId && <BusinessHealthBar businessId={businessId} />}
          </div>

          {/* Vertical divider */}
          <div className="w-px self-stretch bg-light-border dark:bg-dark-border flex-shrink-0" />

          {/* RIGHT — Majengo Profile card */}
          <div className="w-64 flex-shrink-0 rounded-2xl bg-light-card dark:bg-dark-card shadow-soft overflow-hidden flex flex-col">
            {/* Primary header stripe */}
            <div className="flex items-center gap-2 px-4 py-3 bg-primary">
              <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <QrCode size={15} color="#fff" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-white text-xs leading-tight">Majengo Profile</p>
                <p className="text-white/70 text-[10px]">Your public business page</p>
              </div>
              <span className="text-[9px] font-bold text-white bg-white/20 rounded px-1.5 py-0.5 flex-shrink-0 tracking-wide">LIVE</span>
            </div>

            {/* Business logo + name */}
            <div className="flex items-center gap-3 px-4 pt-4 pb-3">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center overflow-hidden flex-shrink-0">
                {business?.logo_url
                  ? <img src={getImageUrl(business.logo_url)} alt={business.name} className="w-full h-full object-cover" />
                  : <span className="text-white font-bold text-sm">{business?.name?.charAt(0).toUpperCase() || 'B'}</span>
                }
              </div>
              <div className="min-w-0">
                <p className="font-bold text-light-text dark:text-dark-text text-sm truncate">{business?.name || 'Business Name'}</p>
                <p className="text-xs text-light-subtle dark:text-dark-subtle truncate">{business?.category || 'Category'}</p>
              </div>
            </div>

            {/* Divider */}
            <div className="mx-4 h-px bg-light-border dark:bg-dark-border" />

            {/* Action buttons stacked */}
            <div className="flex flex-col gap-2 px-4 py-3 mt-auto">
              <button
                onClick={() => navigate(`/business/${businessId}/edit`)}
                className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-semibold hover:opacity-80 transition-opacity"
                style={{ backgroundColor: 'rgba(79,70,229,0.10)', color: '#4F46E5' }}
              >
                <Pencil size={13} /> Edit Profile
              </button>
              <button
                onClick={() => profileSlug && window.open(`https://majengo.co.ke/biz/${profileSlug}`, '_blank')}
                disabled={!profileSlug}
                className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-semibold bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text hover:opacity-80 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ExternalLink size={13} /> View Live
              </button>
            </div>
          </div>
        </div>

        {/* ── Business Management ── */}
        {/* cards: Majengo Profile | Services | Products | Staff Management */}
        <div className="mx-6 mt-6 rounded-2xl bg-light-card dark:bg-dark-card shadow-lg" style={{ overflow: 'visible' }}>
          <p className="px-6 pt-6 pb-2 text-2xl font-black text-light-text dark:text-dark-text">Business Management</p>
          <OptionGrid items={opts.management} onNav={navigate} />
        </div>

        {/* ── Most used ── */}
        <div className="mx-6 mt-6 rounded-2xl bg-light-card dark:bg-dark-card shadow-lg" style={{ overflow: 'visible' }}>
          <p className="px-6 pt-6 pb-2 text-2xl font-black text-light-text dark:text-dark-text">Most Used</p>
          <OptionGrid items={opts.mostUsed} onNav={navigate} />
        </div>

        {/* ── Daily Operations ── */}
        <div className="mx-6 mt-6 rounded-2xl bg-light-card dark:bg-dark-card shadow-lg" style={{ overflow: 'visible' }}>
          <p className="px-6 pt-6 pb-2 text-2xl font-black text-light-text dark:text-dark-text">Daily Operations</p>
          <OptionGrid items={opts.operations} onNav={navigate} />
        </div>

        {/* ── Setup and Configuration ── */}
        <div className="mx-6 mt-6 rounded-2xl bg-light-card dark:bg-dark-card shadow-lg" style={{ overflow: 'visible' }}>
          <p className="px-6 pt-6 pb-2 text-2xl font-black text-light-text dark:text-dark-text">Setup and Configuration</p>
          {/* M-Pesa Setup */}
          <button
            onClick={() => navigate(`/business/${businessId}/mpesa`)}
            className="w-full flex items-center gap-4 px-5 py-4 hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-emerald-100 dark:bg-emerald-900/30">
              <span className="text-emerald-600 dark:text-emerald-400 font-bold text-sm">M</span>
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-light-text dark:text-dark-text">M-Pesa Setup</p>
              <p className="text-xs text-light-subtle dark:text-dark-subtle">Configure mobile money payments</p>
            </div>
          </button>

          {/* Contact Information */}
          <div className="px-5 py-2">
            <p className="text-sm font-bold text-light-text dark:text-dark-text mb-3">Contact Information</p>
            {/* Phone */}
            <div className="flex items-center gap-3 py-2">
              <Phone size={18} className={business?.primary_contact_phone ? 'text-primary' : 'text-red-500'} />
              {business?.primary_contact_phone
                ? <span className="text-sm text-light-text dark:text-dark-text">{business.primary_contact_phone}</span>
                : <span className="flex items-center gap-1.5 text-sm text-light-subtle dark:text-dark-subtle">
                    <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                    Phone number not set
                  </span>
              }
            </div>
            {/* Email */}
            <div className="flex items-center gap-3 py-2 pb-4">
              <Mail size={18} className={business?.business_email ? 'text-primary' : 'text-red-500'} />
              {business?.business_email
                ? <span className="text-sm text-light-text dark:text-dark-text">{business.business_email}</span>
                : <span className="flex items-center gap-1.5 text-sm text-light-subtle dark:text-dark-subtle">
                    <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                    Business email not set
                  </span>
              }
            </div>
          </div>
        </div>

        {/* ── Analytics and Growth ── */}
        <div className="mx-6 mt-6 rounded-2xl bg-light-card dark:bg-dark-card shadow-lg" style={{ overflow: 'visible' }}>
          <p className="px-6 pt-6 pb-2 text-2xl font-black text-light-text dark:text-dark-text">Analytics and Growth</p>
          <OptionGrid items={opts.analyticsAndGrowth} onNav={navigate} />
        </div>

        {/* ── About This Business ── */}
        {business?.description && (
          <div className="mx-6 mt-6 rounded-2xl bg-light-card dark:bg-dark-card shadow-lg p-6">
            <p className="text-2xl font-black text-light-text dark:text-dark-text mb-4">About This Business</p>
            <p className="text-sm text-light-text dark:text-dark-text leading-relaxed">{business.description}</p>
          </div>
        )}

        <div className="h-24" />
      </div>

      {/* ── AI Chat button ── */}
      {businessId && <AIChatButton businessId={businessId} />}

      {/* ── Business switcher bottom sheet ── */}
      {showPicker && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end" onClick={() => setShowPicker(false)}>
          <div
            className="w-full bg-light-card dark:bg-dark-card rounded-t-3xl p-5 pb-12 max-h-[70vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="w-10 h-1 bg-light-border dark:bg-dark-border rounded-full mx-auto mb-5" />
            <div className="max-w-2xl mx-auto">
              <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-4">Switch Business</h3>
              <div className="space-y-2">
                {allBusinesses.map(biz => (
                  <button
                    key={biz.id}
                    onClick={() => { setShowPicker(false); navigate(`/business/${biz.id}`); }}
                    className={`w-full flex items-center gap-3 p-3.5 rounded-2xl transition-colors text-left ${
                      biz.id === businessId
                        ? 'bg-primary/10'
                        : 'bg-light-bg dark:bg-dark-bg hover:bg-light-card dark:hover:bg-dark-card'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center overflow-hidden flex-shrink-0">
                      {biz.logo_url
                        ? <img src={getImageUrl(biz.logo_url)} alt={biz.name} className="w-full h-full object-cover" />
                        : <span className="text-white font-bold">{biz.name.charAt(0)}</span>
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-light-text dark:text-dark-text truncate">{biz.name}</p>
                      <p className="text-xs text-light-subtle dark:text-dark-subtle truncate">{biz.category}</p>
                    </div>
                    {biz.id === businessId && (
                      <span className="text-[11px] font-bold text-primary bg-primary/10 px-2.5 py-1 rounded-full flex-shrink-0">Active</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessDashboard;
